// Timeline = interleaved record of text + activity (tool calls, subagent
// transitions, artifacts) in the order the agent emitted them. Used by the
// chat renderer so reasoning between tool calls shows as separate bubbles
// instead of being smashed into one big text block.

import type { ActivityEntry, Message, TimelineSegment } from "./types";

/** Append a text chunk to the trailing text segment, or start a new one if
 * the last segment is an activity. Returns a NEW array (immutable update). */
export function appendTextChunk(
  segments: TimelineSegment[],
  chunk: string
): TimelineSegment[] {
  if (!chunk) return segments;
  const last = segments[segments.length - 1];
  if (last && last.kind === "text") {
    return [
      ...segments.slice(0, -1),
      { kind: "text", text: last.text + chunk },
    ];
  }
  return [...segments, { kind: "text", text: chunk }];
}

/** Push a new activity segment. Subsequent text chunks will start a new
 * text segment after this divider. */
export function pushActivity(
  segments: TimelineSegment[],
  entry: ActivityEntry
): TimelineSegment[] {
  return [...segments, { kind: "activity", entry }];
}

/** Mutate an existing tool segment in place when its result lands. Matches
 * by tool_use_id. Returns a NEW array. */
export function updateActivity(
  segments: TimelineSegment[],
  entry: Extract<ActivityEntry, { kind: "tool" }>
): TimelineSegment[] {
  return segments.map((seg) => {
    if (
      seg.kind === "activity" &&
      seg.entry.kind === "tool" &&
      seg.entry.tool_use_id === entry.tool_use_id
    ) {
      return { kind: "activity" as const, entry };
    }
    return seg;
  });
}

/** Back-compat: messages persisted before the segments field existed have
 * `content` + `activity[]`. Reconstruct an approximate timeline (text first,
 * tools after) so the renderer can use a single code path. */
export function buildLegacyTimeline(message: Message): TimelineSegment[] {
  const segs: TimelineSegment[] = [];
  if (message.content) {
    segs.push({ kind: "text", text: message.content });
  }
  for (const entry of message.activity ?? []) {
    segs.push({ kind: "activity", entry });
  }
  return segs;
}

/** Choose the right timeline source for a persisted message. */
export function timelineFromMessage(message: Message): TimelineSegment[] {
  if (Array.isArray(message.segments) && message.segments.length > 0) {
    return message.segments;
  }
  return buildLegacyTimeline(message);
}

/** Group consecutive activity segments into runs so the renderer can wrap
 * them in a single Terminal box. Special artifacts (ask_user_question,
 * images) are extracted into their own groups so the renderer can show
 * dedicated UI for them — bundling them inside the terminal would either
 * hide the interactive answer card or replace a real image gallery with
 * a generic "◆ artifact · images" line. */
export type RenderGroup =
  | { kind: "text"; text: string }
  | { kind: "tool_run"; entries: ActivityEntry[] }
  | {
      kind: "ask_user_question";
      entry: Extract<ActivityEntry, { kind: "artifact" }>;
    }
  | {
      kind: "image_gallery";
      entry: Extract<ActivityEntry, { kind: "artifact" }>;
    }
  | {
      kind: "product_catalog";
      entry: Extract<ActivityEntry, { kind: "artifact" }>;
    };

export function groupForRender(segments: TimelineSegment[]): RenderGroup[] {
  const groups: RenderGroup[] = [];
  let currentRun: ActivityEntry[] | null = null;

  const flush = () => {
    if (currentRun && currentRun.length) {
      groups.push({ kind: "tool_run", entries: currentRun });
    }
    currentRun = null;
  };

  for (const seg of segments) {
    if (seg.kind === "text") {
      flush();
      // Skip empty text segments — they happen when a tool comes immediately
      // after another tool with no agent commentary in between.
      if (seg.text.trim().length > 0) {
        groups.push({ kind: "text", text: seg.text });
      }
      continue;
    }
    const entry = seg.entry;
    if (
      entry.kind === "artifact" &&
      entry.kind_subtype === "ask_user_question"
    ) {
      flush();
      groups.push({ kind: "ask_user_question", entry });
      continue;
    }
    if (entry.kind === "artifact" && entry.kind_subtype === "images") {
      flush();
      groups.push({ kind: "image_gallery", entry });
      continue;
    }
    if (entry.kind === "artifact" && entry.kind_subtype === "product_catalog") {
      flush();
      groups.push({ kind: "product_catalog", entry });
      continue;
    }
    if (!currentRun) currentRun = [];
    currentRun.push(entry);
  }
  flush();
  return groups;
}
